import React from "react";
import PropTypes from "prop-types";
import { X, Sliders, Info, MessageSquare } from "lucide-react";
import Button from "../Button/Button.js";
import Dropdown from "../Dropdown/Dropdown.js";
import "./VariablesSidebar.css";

const VariablesSidebar = ({
  isOpen,
  onClose,
  activeSectionId = "all",
  activeSectionTitle = "All Sections",
  sectionVarsMap = {},
  combinedVars = [],
  getVariableValue,
  handleVariableChange,
  preProcessingOptions = [],
  dynamicSectionOptions = null,
  onSectionFilterChange
}) => {
  if (!isOpen) return null;

  const currentVars = activeSectionId && activeSectionId !== "all" && sectionVarsMap[activeSectionId]
    ? sectionVarsMap[activeSectionId]
    : combinedVars;

  const displayVars = Array.from(new Set(currentVars));

  const sectionOptions = dynamicSectionOptions && dynamicSectionOptions.length > 0
    ? dynamicSectionOptions
    : [
        { value: "all", label: "All Sections" },
        { value: "welcome_message", label: "Welcome Message / Initial Greeting" },
        { value: "conversation_flow", label: "Conversation Flow" },
        { value: "special_cases", label: "Special Cases" },
        { value: "rebuttals_faqs", label: "Rebuttals / FAQs" },
        { value: "final_call_message", label: "Final Call Message" }
      ];

  return (
    <div className="variables-sidebar-overlay" onClick={onClose}>
      <div className="variables-sidebar-container" onClick={(e) => e.stopPropagation()}>
        <div className="variables-sidebar-header">
          <div className="variables-sidebar-title-group">
            <Sliders size={20} style={{ color: "var(--primary-accent)" }} />
            <div>
              <h3 className="variables-sidebar-title">Variables Settings</h3>
              <div className="variables-sidebar-subtitle">
                Showing variables for <strong style={{ color: "#2563eb" }}>{activeSectionTitle}</strong>
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} style={{ width: "32px", height: "32px", padding: 0 }}>
            <X size={16} />
          </Button>
        </div>

        <div className="variables-sidebar-content">
          {/* Custom Dropdown for Section Filter */}
          <div style={{ marginBottom: "16px" }}>
            <Dropdown
              options={sectionOptions}
              value={activeSectionId}
              placeholder="Filter by Script Section"
              onChange={(e) => {
                const selectedVal = e.target.value;
                const opt = sectionOptions.find(o => o.value === selectedVal);
                if (onSectionFilterChange && opt) {
                  onSectionFilterChange(opt.value, opt.label);
                }
              }}
            />
          </div>

          <div className="variables-testing-header" style={{ marginBottom: "20px", background: "var(--slate-50)", padding: "10px 12px", borderRadius: "8px", border: "1px solid var(--slate-100)", display: "flex", alignItems: "center" }}>
            <Info size={14} style={{ color: "var(--primary-accent)", marginRight: "6px", flexShrink: 0 }} />
            <span style={{ fontSize: "12px", color: "var(--slate-600)", fontWeight: "500" }}>Configure pre-processing and default values for variables in this section.</span>
          </div>

          <div className="variables-section-card">
            <div className="variables-section-header">
              <MessageSquare size={14} style={{ color: "var(--primary-accent)" }} />
              <span className="variables-section-title">Section Variables</span>
              <span className="badge-pill" style={{ marginLeft: "auto", fontSize: "11px", background: "#dbeafe", color: "#1d4ed8", padding: "2px 8px", borderRadius: "10px", fontWeight: "700" }}>
                {displayVars.length} variables
              </span>
            </div>
            <div className="variables-section-body">
              {displayVars.length === 0 ? (
                <div className="no-variables-placeholder" style={{ textAlign: "center", padding: "24px 12px", color: "#94a3b8", fontSize: "13px" }}>
                  No variables found in {activeSectionTitle}.
                </div>
              ) : (
                displayVars.map((varName) => {
                  const currentOpt = getVariableValue(varName, "option") || "";
                  const currentVal = getVariableValue(varName, "value") || "";

                  return (
                    <div key={varName} className="variable-row-card" style={{ display: "flex", flexDirection: "column", gap: "8px", background: "var(--gray-50)", border: "1px solid var(--gray-100)", borderRadius: "8px", padding: "10px" }}>
                      <div className="variable-key-badge-container" style={{ width: "100%" }}>
                        <span className="variable-key-badge" style={{ fontSize: "12px", fontWeight: "700", color: "#6b21a8", background: "#f3e8ff", border: "1px solid #e9d5ff", padding: "3px 8px", borderRadius: "6px" }}>
                          {`{${varName}}`}
                        </span>
                      </div>
                      
                      <div style={{ display: "flex", gap: "8px", width: "100%" }}>
                        <div className="variable-select-wrapper" style={{ flex: 1 }}>
                          <Dropdown
                            options={preProcessingOptions}
                            value={currentOpt}
                            placeholder="Pre-processing..."
                            onChange={(e) => handleVariableChange(varName, "option", e.target.value)}
                          />
                        </div>

                        <div className="variable-input-wrapper" style={{ flex: 1 }}>
                          <input
                            type="text"
                            placeholder="Test value..."
                            value={currentVal}
                            onChange={(e) => handleVariableChange(varName, "value", e.target.value)}
                            className="field-input-textarea"
                            style={{ height: "36px", fontSize: "12px", width: "100%" }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

VariablesSidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  parsedCF: PropTypes.array,
  parsedSC: PropTypes.array,
  parsedRF: PropTypes.array,
  getVariableValue: PropTypes.func.isRequired,
  handleVariableChange: PropTypes.func.isRequired,
  preProcessingOptions: PropTypes.array
};

export default VariablesSidebar;
